import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    TableSortLabel,
    Tooltip,
    Box,
    Menu,
    Link,
    styled,
    alpha
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import dayjs from 'dayjs';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import { TABLE_COLUMNS, COMPLEXITY_DETAILS } from '../common';

const StyledMenuBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    gap: theme.spacing(2)
}));

const menuLinkStyles = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    height: '32px',
    padding: '8px 12px',
    border: '1px solid',
    borderColor: 'primary.main',
    borderRadius: '4px',
    color: 'primary.main',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: 400,
    cursor: 'pointer',
    pl: 3,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        textDecoration: 'none',
        borderColor: theme.palette.primary.dark,
        color: theme.palette.primary.dark
    },
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.primary.main, 0.1)
    }
});

const deleteLinkStyles = (theme) => ({
    ...menuLinkStyles(theme),
    borderColor: 'error.main',
    color: 'error.main',
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        textDecoration: 'none',
        borderColor: 'error.dark',
        color: 'error.dark'
    },
    '&:focus': {
        outline: 'none',
        backgroundColor: alpha(theme.palette.error.main, 0.1)
    }
});

const GoalTable = ({ goals, sort, onSort }) => {
    const dispatch = useDispatch();
    const departments = useSelector(selectors.departmentsSelector);
    const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
    const [activeGoal, setActiveGoal] = React.useState(null);

    const handleEdit = (goal) => {
        dispatch(actions.openGoalDialog(goal));
    };

    const handleDelete = (goal) => {
        dispatch(actions.openDeleteDialog(goal));
    };

    const openMenu = (event, goal) => {
        setMenuAnchorEl(event.currentTarget);
        setActiveGoal(goal);
    };

    const closeMenu = () => {
        setMenuAnchorEl(null);
        setActiveGoal(null);
    };

    const getComplexityDetails = (complexity) => {
        const details = COMPLEXITY_DETAILS[complexity?.toLowerCase()];
        if (!details) {
            return {
                label: complexity?.toUpperCase() || 'N/A',
                color: 'text.disabled',
                description: ''
            };
        }
        return details;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return dayjs(dateString).format('MMM DD, YYYY');
        } catch {
            return dateString;
        }
    };

    return (
        <>
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {TABLE_COLUMNS.map((column) => (
                            <TableCell
                                key={column.id}
                                align={column.align || 'left'}
                                style={{ minWidth: column.minWidth }}
                                sx={{
                                    fontWeight: 600,
                                    backgroundColor: 'action.hover',
                                    borderBottom: '2px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                {column.sortable ? (
                                    <TableSortLabel
                                        active={sort.sortBy === column.id}
                                        direction={sort.sortBy === column.id && sort.sortDescending ? 'desc' : 'asc'}
                                        onClick={() => onSort(column.id)}
                                    >
                                        {column.label}
                                    </TableSortLabel>
                                ) : (
                                    column.label
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {goals.map((goal) => (
                        <TableRow
                            key={goal.id}
                            hover
                            sx={(theme) => ({
                                '&:last-child td': { border: 0 },
                                '&:hover': { backgroundColor: alpha(theme.palette.divider, 0.06) }
                            })}
                        >
                            <TableCell>
                                <strong>{goal.name}</strong>
                            </TableCell>
                            <TableCell>
                                <Tooltip title={typeof goal.description === 'string' ? goal.description : 'No description'} placement="top">
                                    <span
                                        style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {typeof goal.description === 'string' ? goal.description : 'No description'}
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                {(() => {
                                    const complexityInfo = getComplexityDetails(goal.complexity);
                                    return (
                                        <Tooltip title={complexityInfo.description} placement="top">
                                            <Chip
                                                label={complexityInfo.label}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    borderColor: complexityInfo.color,
                                                    color: complexityInfo.color,
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase'
                                                }}
                                            />
                                        </Tooltip>
                                    );
                                })()}
                            </TableCell>
                            <TableCell>
                                {(() => {
                                    // Handle multiple departments (new format)
                                    if (goal.departmentIds && Array.isArray(goal.departmentIds) && goal.departmentIds.length > 0) {
                                        const deptNames = goal.departmentIds
                                            .map(deptId => {
                                                const dept = departments.find(d => d.id === deptId || d.id === parseInt(deptId));
                                                return dept ? dept.name : null;
                                            })
                                            .filter(name => name !== null);
                                        
                                        if (deptNames.length > 0) {
                                            const visibleDepts = deptNames.slice(0, 2);
                                            const remainingCount = deptNames.length - 2;
                                            const remainingDepts = deptNames.slice(2);
                                            
                                            return (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                                    {visibleDepts.map((name, idx) => (
                                                        <Chip key={idx} label={name} size="small" variant="outlined" />
                                                    ))}
                                                    {remainingCount > 0 && (
                                                        <Tooltip title={remainingDepts.join(', ')} placement="top">
                                                            <Chip 
                                                                label={`+${remainingCount} more`} 
                                                                size="small" 
                                                                variant="outlined"
                                                                sx={{ 
                                                                    fontStyle: 'italic',
                                                                    opacity: 0.7,
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        opacity: 1,
                                                                        backgroundColor: 'action.hover'
                                                                    }
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            );
                                        }
                                    }
                                    
                                    // Handle single department from API (if populated)
                                    if (goal.department?.name) {
                                        return <Chip label={goal.department.name} size="small" variant="outlined" />;
                                    }
                                    
                                    // Legacy: single departmentId
                                    if (goal.departmentId && departments.length > 0) {
                                        const dept = departments.find(d => d.id === goal.departmentId || d.id === parseInt(goal.departmentId));
                                        if (dept) {
                                            return <Chip label={dept.name} size="small" variant="outlined" />;
                                        }
                                    }
                                    
                                    // No department assigned -> show All Departments
                                    return <Chip label={'All Departments'} size="small" variant="outlined" />;
                                })()}
                            </TableCell>
                            <TableCell>{goal.updatedBy || goal.createdBy || '-'}</TableCell>
                            <TableCell>{formatDate(goal.updatedAt || goal.createdAt)}</TableCell>
                            <TableCell align="center">
                                <Tooltip title="More" placement="top">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => openMenu(e, goal)}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
        <Menu
            id="goal-actions-menu"
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={closeMenu}
            MenuListProps={{
                'aria-labelledby': 'goal-actions-button'
            }}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <StyledMenuBox>
                <Link
                    component="button"
                    onClick={(e) => {
                        e.preventDefault();
                        if (activeGoal) handleEdit(activeGoal);
                        closeMenu();
                    }}
                    sx={(theme) => menuLinkStyles(theme)}
                >
                    <EditIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                    Edit
                </Link>
                <Link
                    component="button"
                    onClick={(e) => {
                        e.preventDefault();
                        if (activeGoal) handleDelete(activeGoal);
                        closeMenu();
                    }}
                    sx={(theme) => deleteLinkStyles(theme)}
                >
                    <DeleteIcon sx={{ marginRight: 1, fontSize: '18px' }} />
                    Delete
                </Link>
            </StyledMenuBox>
        </Menu>
        </>
    );
};

export default GoalTable;
